/**
 * iter415 #4 — Change Orders page (/change-orders). Mirrors the Purchasing › Purchase Orders list:
 * same toolbar (search · group pills · view pills · count/total), same sortable/filterable headers,
 * same status pill-as-control, same expandable line rows. Default view = every active CO
 * (draft, submitted, approved); Declined / Not approved are archived behind a divider pill.
 */
import { useState, useEffect, useMemo, useCallback, Fragment } from "react";
import { tr } from "@/i18n";
import { toast } from "sonner";
import { ChevronDown, ChevronRight } from "lucide-react";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from "@/components/ui/dropdown-menu";
import { useNavigate } from "react-router-dom";
import SearchBar from "@/components/common/SearchBar";
import { PillRail, Pill, PillDivider } from "@/components/common/Segmented";
import StickyTop from "@/components/common/StickyTop";
import { SortableHeader, HeaderFilter } from "@/components/common/SortHeader";
import { FoundationSyncPill, isSyncLocked } from "@/components/integrations/FoundationSyncPill";
import { shortDate, money } from "@/lib/format";
import { setFocusTarget, FOCUS_CO } from "@/lib/deepLinkFocus";
import { useAuth } from "@/auth/AuthContext";
import {
  listChangeOrders, approveChangeOrder, notApproveChangeOrder, revertCOToDraft, unapproveChangeOrder, retryCOFoundationSync,
} from "@/lib/api";
import { CO_STATUS_LABEL, CO_STATUS_ORDER, CO_STATUS_TONE } from "@/components/changeOrders/COStatusPill";

const ACTIVE_CO = ["draft", "submitted", "approved"];
const CO_VIEWS = [
  { key: "draft", label: "Draft", title: "Being priced — not sent to the customer yet" },
  { key: "submitted", label: "Submitted", title: "Sent to the customer — awaiting their answer" },
  { key: "approved", label: "Approved", title: "Approved — part of the contract" },
  { key: "declined", label: "Not approved", dividerBefore: true, title: "Archived — declined by the customer or marked not approved" },
];
const inCOView = (co, view) => {
  if (view === "declined") return co.status === "declined" || co.status === "not_approved";
  if (view) return co.status === view;
  return ACTIVE_CO.includes(co.status);
};
const coHaystack = (co) => [
  co.co_number, co.description, co.wo_number, co.wo_title, co.client_name, co.site_name, co.project_manager,
  CO_STATUS_LABEL[co.status] || co.status, co.foundation_status,
  ...(co.line_items || []).flatMap((li) => [li.description, li.part_number]),
].filter(Boolean).join(" ").toLowerCase();
const coWoName = (co) => co.wo_title || (co.wo_number ? `WO ${co.wo_number}` : "No work order");
const statusIdx = (s) => { const i = CO_STATUS_ORDER.indexOf(s); return i === -1 ? CO_STATUS_ORDER.length : i; };
const coSortValue = (co, field) => {
  switch (field) {
    case "co_number": return (co.co_number || "").toLowerCase();
    case "wo": return coWoName(co).toLowerCase();
    case "description": return (co.description || "").toLowerCase();
    case "customer": return (co.client_name || "").toLowerCase();
    case "pm": return (co.project_manager || "").toLowerCase();
    case "subtotal": return co.subtotal || 0;
    case "total": return co.total || 0;
    case "status": return statusIdx(co.status);
    case "foundation": return co.foundation_status || "";
    case "created": return co.created_at || "";
    case "submitted": return co.submitted_at || "";
    case "approved": return co.approved_at || "";
    default: return "";
  }
};

const COTh = ({ field, children, sort, onSort, className = "", filter }) => (
  <TableHead className={`text-xs ${className}`}>
    <SortableHeader label={children} sorted={sort.field === field} direction={sort.dir} onSort={() => onSort(field)} testId={`co-sort-${field}`} filter={filter} />
  </TableHead>
);

const CO_COLS = 12;

// The status pill IS the status control — the same moves the CO card offers (approve / not approved /
// revise / cancel approval), nothing that emails the customer. Locked (synced) COs can't move.
const nextStatuses = (co) => {
  if (co.status === "draft") return co.items_total || co.scopes_count ? [{ value: "approved", label: "Approve" }] : [];
  if (co.status === "submitted") return [{ value: "approved", label: "Approve" }, { value: "not_approved", label: "Not approved" }, { value: "draft", label: "Revise (back to draft)" }];
  if (co.status === "approved") return isSyncLocked(co.foundation_sync) ? [] : [{ value: "draft", label: "Cancel approval" }];
  return [{ value: "draft", label: "Revise (back to draft)" }];
};
const COStatusMenu = ({ co, onPick, canWrite }) => {
  const opts = canWrite ? nextStatuses(co) : [];
  const pill = (
    <span data-testid={`co-list-status-${co.id}`}
      className={`inline-flex min-w-[5.5rem] items-center justify-center gap-1 whitespace-nowrap rounded-full px-2 py-0.5 text-[10px] font-medium ${CO_STATUS_TONE[co.status] || "bg-slate-100 text-slate-600"}`}>
      {CO_STATUS_LABEL[co.status] || co.status || "—"}{opts.length > 0 && <ChevronDown className="h-2.5 w-2.5 opacity-60" />}
    </span>
  );
  if (!opts.length) return pill;
  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild><button type="button" title={tr("Click to change status")}>{pill}</button></DropdownMenuTrigger>
      <DropdownMenuContent align="start" data-testid={`co-list-status-menu-${co.id}`}>
        {opts.map((o) => (
          <DropdownMenuItem key={o.value} onClick={() => onPick(co, o.value)} data-testid={`co-list-status-option-${o.value}-${co.id}`}>{o.label}</DropdownMenuItem>
        ))}
      </DropdownMenuContent>
    </DropdownMenu>
  );
};

const COST_TYPE_LABEL = { material: "Material", labor: "Labor", freight: "Freight", equipment: "Equipment", travel: "Travel", disposal: "Disposal", subcontract: "Subcontract", misc: "Misc",
  "1": "Labor", "2": "Material", "3": "Subcontract", "4": "Freight", "5": "Equipment", "6": "Labor Burden", "7": "Other" };
const TH = "py-1 pr-3 text-right";
const COLinesRow = ({ co }) => (
  <TableRow className="bg-slate-50/60 hover:bg-slate-50/60" data-testid={`co-lines-${co.id}`}>
    <TableCell colSpan={CO_COLS} className="p-0">
      <div className="px-10 py-2">
        {(co.line_items || []).length === 0 ? (
          <div className="text-xs text-slate-400">{tr("No priced lines on this change order yet.")}</div>
        ) : (
          <table className="w-full text-xs">
            <thead>
              <tr className="text-[11px] font-semibold text-slate-600">
                <th className="w-[120px] py-1 pr-3 text-left">{tr("Part #")}</th>
                <th className="py-1 pr-3 text-left">{tr("Description")}</th>
                <th className="w-[110px] py-1 pr-3 text-left">{tr("Type")}</th>
                <th className={`w-[72px] ${TH}`}>{tr("Qty")}</th>
                <th className={`w-[110px] ${TH}`}>{tr("Unit Price")}</th>
                <th className={`w-[120px] ${TH}`}>{tr("Total")}</th>
              </tr>
            </thead>
            <tbody>
              {co.line_items.map((li, i) => (
                <tr key={li.id || i} className="border-t border-slate-200/70" data-testid={`co-line-${co.id}-${li.id || i}`}>
                  <td className="py-1 pr-3 font-mono text-slate-600">{li.part_number || "—"}</td>
                  <td className="py-1 pr-3 text-slate-700">{li.description || "—"}</td>
                  <td className="py-1 pr-3 text-slate-500">{COST_TYPE_LABEL[li.cost_type] || li.cost_type || "—"}</td>
                  <td className={`${TH} num`}>{li.quantity ?? "—"}</td>
                  <td className={`${TH} num text-slate-600`}>{money(li.unit_price)}</td>
                  <td className={`${TH} num ${li.total < 0 ? "text-red-700" : ""}`}>{money(li.total)}</td>
                </tr>
              ))}
            </tbody>
            <tfoot>
              <tr className="border-t border-slate-300 text-[11px] font-semibold text-slate-700" data-testid={`co-lines-totals-${co.id}`}>
                <td className="py-1 pr-3" colSpan={3}>{tr("CO totals")}</td>
                <td className={`${TH} text-slate-500 font-normal whitespace-nowrap`} colSpan={2}>
                  {tr("Subtotal")} {money(co.subtotal)} · {tr("Tax")}{co.tax_manual ? " (manual)" : co.tax_rate ? ` ${co.tax_rate}%` : ""} {money(co.tax_amount)}
                </td>
                <td className={`${TH} num ${co.total < 0 ? "text-red-700" : ""}`}>{money(co.total)}</td>
              </tr>
            </tfoot>
          </table>
        )}
      </div>
    </TableCell>
  </TableRow>
);

export default function ChangeOrders() {
  const navigate = useNavigate();
  const { can } = useAuth();
  const canWrite = can("ops.work_orders", "write");
  const [all, setAll] = useState([]);
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState("");
  const [view, setView] = useState("");
  const [groupBy, setGroupBy] = useState(""); // "" | "status" | "wo"
  const [colFilters, setColFilters] = useState({ wo: "", customer: "", pm: "", status: "" });
  const setColFilter = (k, v) => setColFilters((p) => ({ ...p, [k]: v || "" }));
  const [expanded, setExpanded] = useState(() => new Set());
  const [sort, setSort] = useState({ field: "", dir: "asc" });

  const openWO = (co) => {
    if (!co.wo_id) return;
    setFocusTarget(FOCUS_CO, co.id);
    navigate(`/work-orders/${co.wo_id}`, { state: { openWoTab: "change_orders", returnTo: "/change-orders" } });
  };

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await listChangeOrders();
      setAll(res.data?.rows || []);
    } catch {
      toast.error("Failed to load change orders");
    } finally {
      setLoading(false);
    }
  }, []);
  useEffect(() => { load(); }, [load]);

  const changeStatus = async (co, status) => {
    try {
      if (status === "approved") { await approveChangeOrder(co.wo_id, co.id); toast.success(`${co.co_number} approved`); }
      else if (status === "not_approved") { await notApproveChangeOrder(co.wo_id, co.id); toast.success(`${co.co_number} marked not approved`); }
      else if (status === "draft" && co.status === "approved") { await unapproveChangeOrder(co.wo_id, co.id); toast.success(`${co.co_number} approval cancelled`); }
      else if (status === "draft") { await revertCOToDraft(co.wo_id, co.id); toast.success(`${co.co_number} back to draft`); }
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not update status"); }
  };

  const onSort = (field) => setSort((p) => (p.field === field ? { field, dir: p.dir === "asc" ? "desc" : "asc" } : { field, dir: "asc" }));

  const rows = useMemo(() => {
    const q = search.trim().toLowerCase();
    const cf = colFilters;
    let out = all.filter((co) => (q ? coHaystack(co).includes(q) : inCOView(co, view)))
      .filter((co) => (!cf.wo || coWoName(co) === cf.wo) && (!cf.customer || (co.client_name || "—") === cf.customer)
        && (!cf.pm || (co.project_manager || "—") === cf.pm) && (!cf.status || co.status === cf.status));
    if (sort.field) {
      out = [...out].sort((a, b) => {
        const av = coSortValue(a, sort.field); const bv = coSortValue(b, sort.field);
        if (av < bv) return sort.dir === "asc" ? -1 : 1;
        if (av > bv) return sort.dir === "asc" ? 1 : -1;
        return 0;
      });
    }
    return out;
  }, [all, search, view, sort, colFilters]);

  const filterOpts = useMemo(() => {
    const q = search.trim().toLowerCase();
    const base = all.filter((co) => (q ? coHaystack(co).includes(q) : inCOView(co, view)));
    const uniq = (fn) => [...new Set(base.map(fn).filter(Boolean))].sort((a, b) => a.localeCompare(b)).map((v) => ({ value: v, label: v }));
    return {
      wo: uniq(coWoName), customer: uniq((co) => co.client_name || "—"), pm: uniq((co) => co.project_manager || "—"),
      status: [...new Set(base.map((co) => co.status).filter(Boolean))].sort((a, b) => statusIdx(a) - statusIdx(b)).map((s) => ({ value: s, label: CO_STATUS_LABEL[s] || s })),
    };
  }, [all, search, view]);

  const grouped = useMemo(() => {
    if (!groupBy) return null;
    const by = new Map();
    const keyOf = groupBy === "wo" ? coWoName : (co) => co.status || "—";
    rows.forEach((co) => { const k = keyOf(co); if (!by.has(k)) by.set(k, []); by.get(k).push(co); });
    const entries = [...by.entries()];
    if (groupBy === "status") entries.sort((a, b) => statusIdx(a[0]) - statusIdx(b[0]));
    else entries.sort((a, b) => a[0].localeCompare(b[0]));
    return entries.map(([key, list]) => ({ key, label: groupBy === "status" ? (CO_STATUS_LABEL[key] || key) : key, rows: list }));
  }, [rows, groupBy]);

  const toggleExpanded = (key) => setExpanded((prev) => { const n = new Set(prev); if (n.has(key)) n.delete(key); else n.add(key); return n; });
  const pickView = (key) => { setSearch(""); setView((cur) => (cur === key ? "" : key)); };
  const total = useMemo(() => rows.reduce((s, co) => s + (co.total || 0), 0), [rows]);
  const allOpen = rows.length > 0 && rows.every((co) => expanded.has(co.row_key));
  const toggleAllExpanded = () => setExpanded(allOpen ? new Set() : new Set(rows.map((co) => co.row_key)));

  const renderRow = (co) => {
    const isOpen = expanded.has(co.row_key);
    const lines = (co.line_items || []).length;
    return (
      <Fragment key={co.row_key}>
        <TableRow data-testid={`co-row-${co.id}`} className={isOpen ? "bg-brand/5" : ""}>
          <TableCell className="px-1">
            <button type="button" onClick={() => toggleExpanded(co.row_key)} aria-expanded={isOpen} aria-label={isOpen ? "Collapse line items" : "Expand line items"}
              title={lines ? `${isOpen ? "Hide" : "Show"} ${lines} line item${lines === 1 ? "" : "s"}` : "No line items"}
              className="inline-flex h-5 w-5 items-center justify-center rounded text-slate-400 hover:bg-slate-100 hover:text-slate-700" data-testid={`co-expand-${co.id}`}>
              {isOpen ? <ChevronDown className="h-3.5 w-3.5" /> : <ChevronRight className="h-3.5 w-3.5" />}
            </button>
          </TableCell>
          <TableCell className="font-mono text-xs font-semibold text-brand whitespace-nowrap">
            <button type="button" className="cursor-pointer underline decoration-dotted decoration-brand/40 underline-offset-2 hover:decoration-solid" onClick={() => openWO(co)}
              title={`Open ${co.wo_number} → Change Orders · ${co.co_number}`} data-testid={`co-open-${co.id}`}>{co.co_number || "—"}</button>
          </TableCell>
          <TableCell className="max-w-[260px] truncate text-xs text-slate-700" title={co.wo_title || ""} data-testid={`co-list-wo-${co.id}`}>{co.wo_title || "—"}</TableCell>
          <TableCell className="max-w-[260px] truncate text-xs text-slate-600" title={co.description || ""}>{co.description || <span className="text-slate-400">No description</span>}</TableCell>
          <TableCell className="text-xs text-slate-600">{co.client_name || "—"}</TableCell>
          <TableCell className="text-xs text-slate-600">{co.project_manager || "—"}</TableCell>
          <TableCell className={`text-xs num ${co.subtotal < 0 ? "text-red-700" : "text-slate-600"}`}>{money(co.subtotal)}</TableCell>
          <TableCell className={`text-xs font-medium num ${co.total < 0 ? "text-red-700" : ""}`} data-testid={`co-list-total-${co.id}`}>
            {money(co.total)}{co.total < 0 && <span className="ml-1 rounded bg-red-50 px-1 text-[9px] font-medium text-red-700">credit</span>}
          </TableCell>
          <TableCell><COStatusMenu co={co} onPick={changeStatus} canWrite={canWrite} /></TableCell>
          <TableCell className="text-xs whitespace-nowrap">
            {co.status === "approved" ? (
              <FoundationSyncPill sync={co.foundation_sync || { status: "ready", message: "Approved — accounting sends it from Settings › Integrations › Foundation › Sync" }}
                onRetry={canWrite ? () => retryCOFoundationSync(co.wo_id, co.id) : undefined} testId={`co-list-foundation-${co.id}`} showMessage={false} />
            ) : <span className="text-slate-300">—</span>}
          </TableCell>
          <TableCell className="text-xs text-slate-500 num whitespace-nowrap">{shortDate(co.created_at)}</TableCell>
          <TableCell className="text-xs text-slate-500 num whitespace-nowrap" data-testid={`co-list-approved-${co.id}`}>
            {co.status === "approved" ? shortDate(co.approved_at) : co.status === "submitted" ? <span title="Submitted">{shortDate(co.submitted_at)}</span> : "—"}
          </TableCell>
        </TableRow>
        {isOpen && <COLinesRow co={co} />}
      </Fragment>
    );
  };

  const hdr = { sort, onSort };
  const searching = search.trim().length > 0;

  return (
    <div className="space-y-2" data-testid="change-orders-page">
      <StickyTop testId="cos-sticky-top">
        <div className="flex flex-wrap items-center gap-2" data-testid="co-list-toolbar">
          <SearchBar value={search} onChange={setSearch} testId="input-co-search" placeholder={tr("Search...")} />
          <PillRail testId="select-co-status">
            <Pill on={groupBy === "status"} onClick={() => setGroupBy((v) => (v === "status" ? "" : "status"))} testId="co-group-status" title={groupBy === "status" ? "Stop grouping" : "Group the list by status"}>{tr("Status")}</Pill>
            <Pill on={groupBy === "wo"} onClick={() => setGroupBy((v) => (v === "wo" ? "" : "wo"))} testId="co-group-wo" title={groupBy === "wo" ? "Stop grouping" : "Group the list by work order"}>WO</Pill>
            {CO_VIEWS.map((o) => (
              <Fragment key={o.key}>
                {o.dividerBefore && <PillDivider />}
                <Pill on={!searching && view === o.key} title={view === o.key ? "Back to the active list" : o.title} testId={`co-status-${o.key}`} onClick={() => pickView(o.key)}>{o.label}</Pill>
              </Fragment>
            ))}
          </PillRail>
          <div className="ml-auto text-xs text-slate-500" data-testid="cos-count">
            <span className="num">{rows.length}</span> {searching ? "matching" : (view ? CO_VIEWS.find((o) => o.key === view)?.label.toLowerCase() : "active")} CO{rows.length === 1 ? "" : "s"} · <span className="num">{money(total)}</span>
          </div>
        </div>
      </StickyTop>

      <div className="data-table-container">
        <Table data-testid="co-list-table">
          <TableHeader className="thead-sticky">
            <TableRow>
              <TableHead className="w-6 px-1">
                <button type="button" onClick={toggleAllExpanded} disabled={rows.length === 0} aria-label={allOpen ? tr("Collapse all") : tr("Expand all")} title={allOpen ? tr("Collapse all") : tr("Expand all")}
                  className="inline-flex h-5 w-5 items-center justify-center rounded text-slate-400 hover:bg-slate-100 hover:text-slate-700 disabled:opacity-40" data-testid="co-expand-all">
                  {allOpen ? <ChevronDown className="h-3.5 w-3.5" /> : <ChevronRight className="h-3.5 w-3.5" />}
                </button>
              </TableHead>
              <COTh {...hdr} field="co_number" className="w-[130px]">{tr("CO #")}</COTh>
              <COTh {...hdr} field="wo" className="min-w-[200px]" filter={<HeaderFilter label={tr("Name")} testId="co-filter-wo" options={filterOpts.wo} value={colFilters.wo} onChange={(v) => setColFilter("wo", v)} />}>{tr("Name")}</COTh>
              <COTh {...hdr} field="description" className="min-w-[200px]">{tr("Description")}</COTh>
              <COTh {...hdr} field="customer" className="min-w-[160px]" filter={<HeaderFilter label={tr("Customer")} testId="co-filter-customer" options={filterOpts.customer} value={colFilters.customer} onChange={(v) => setColFilter("customer", v)} />}>{tr("Customer")}</COTh>
              <COTh {...hdr} field="pm" className="min-w-[120px]" filter={<HeaderFilter label={tr("PM")} testId="co-filter-pm" options={filterOpts.pm} value={colFilters.pm} onChange={(v) => setColFilter("pm", v)} />}>{tr("PM")}</COTh>
              <COTh {...hdr} field="subtotal" className="w-[110px] num">{tr("Subtotal")}</COTh>
              <COTh {...hdr} field="total" className="w-[120px] num">{tr("Total")}</COTh>
              <COTh {...hdr} field="status" className="w-[130px]" filter={<HeaderFilter label={tr("Status")} testId="co-filter-status" options={filterOpts.status} value={colFilters.status} onChange={(v) => setColFilter("status", v)} />}>{tr("Status")}</COTh>
              <COTh {...hdr} field="foundation" className="w-[150px]">{tr("Foundation")}</COTh>
              <COTh {...hdr} field="created" className="w-[100px] num">{tr("Created")}</COTh>
              <COTh {...hdr} field="approved" className="w-[110px] num">{tr("Approved")}</COTh>
            </TableRow>
          </TableHeader>
          <TableBody>
            {rows.length === 0 ? (
              <TableRow>
                <TableCell colSpan={CO_COLS} className="py-8 text-center text-sm text-slate-400" data-testid="cos-empty-state">
                  {loading ? "Loading…" : "No change orders match the current filters."}
                </TableCell>
              </TableRow>
            ) : grouped ? grouped.map((g) => (
              <Fragment key={g.key}>
                <TableRow className="bg-slate-50 hover:bg-slate-50" data-testid={`co-group-row-${g.key}`}>
                  <TableCell colSpan={CO_COLS} className="py-1.5 text-[11px] font-semibold text-slate-600">
                    {g.label} <span className="font-normal text-slate-400 num">{g.rows.length}</span>
                  </TableCell>
                </TableRow>
                {g.rows.map(renderRow)}
              </Fragment>
            )) : rows.map(renderRow)}
          </TableBody>
        </Table>
      </div>
    </div>
  );
}
